/* eslint-disable spire/export-styles */
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import clearAllProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/ClearAllProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import { AttributeValueFacetModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Hidden from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import GlobeIcon from "@ucs/Icons/Globe";
import LocationIcon from "@ucs/Icons/Location";
import QuestionMarkIcon from "@ucs/Icons/QuestionMark";
import setIsLocalSelectedPLP from "@ucs/Store/Custom/SetIsLocalSelectedPLP";
import { getIsLocalSelectedPLPState } from "@ucs/Store/CustomSelectors";
import { CustomState } from "@ucs/Store/Reducers";
import UCSColors from "@ucs/Utils/UCSColors";
import * as React from "react";
import { FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    description = "description",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.description]: string;
    };
}

const mapStateToProps = (state: CustomState) => ({
    isLoading: state.pages.productList.isLoading,
    selectedAttributesIds: state.pages.productList.productFilters.attributeValueIds,
    isLocalSelectedPLPState: getIsLocalSelectedPLPState(state),
    attributeTypeFacets: getProductListDataViewProperty(state, "attributeTypeFacets"),
});

const mapDispatchToProps = {
    addProductFilters,
    removeProductFilters,
    clearAllProductFilters,
    setIsLocalSelectedPLP,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface LocationFilterStyles {
    wrapperStyles?: InjectableCss;
    textWrapper?: InjectableCss;
    buttonWrapper?: InjectableCss;
    buttonStyle?: ButtonPresentationProps;
    buttonSelected?: ButtonPresentationProps;
    mobileWrapper?: InjectableCss;
    textWrapperMobile?: InjectableCss;
    buttonWrapperMobile?: InjectableCss;
    dismissLink?: LinkPresentationProps;
    buttonStyleMobile?: ButtonPresentationProps;
    buttonSelectedMobile?: ButtonPresentationProps;
    productSelection?: InjectableCss;
    tooltipWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
}

export const locationFilterStyles: LocationFilterStyles = {
    wrapperStyles: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: space-between;
            padding-bottom: 15px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        border-bottom: 1px solid ${UCSColors.gray05};
                    `,
                    css`
                        border-bottom: 1px solid ${UCSColors.gray05};
                    `,
                    css`
                        border-bottom: 1px solid ${UCSColors.gray05};
                    `,
                ]);
            }}
        `,
    },
    textWrapper: {
        css: css`
            font-weight: 500;
            display: none;
            align-items: center;
            padding-right: 10px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        display: flex;
                    `,
                    css`
                        display: flex;
                    `,
                    css`
                        display: flex;
                    `,
                ]);
            }}
        `,
    },
    buttonWrapper: {
        css: css`
            display: none;
            justify-content: space-between;
            align-items: center;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        display: flex;
                    `,
                    css`
                        display: flex;
                    `,
                    css`
                        display: flex;
                    `,
                ]);
            }}
        `,
    },
    buttonStyle: {
        css: css`
            background: white;
            display: flex;
            justify-content: space-between;
            align-items: center;
            text-transform: uppercase;
            font-weight: 500;
            margin: 0 5px;
            span[class^="IconWrapper"] {
                margin-right: 5px;
                color: #007a53;
            }
        `,
    },
    buttonSelected: {
        css: css`
            background: #007a53;
            color: #fff;
            display: flex;
            justify-content: space-between;
            align-items: center;
            text-transform: uppercase;
            font-weight: 500;
            margin: 0 5px;
            &:hover {
                color: #fff !important;
            }
            span[class^="IconWrapper"] {
                margin-right: 5px;
                color: #fff;
            }
        `,
    },
    mobileWrapper: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
        `,
    },
    textWrapperMobile: {
        css: css`
            border-bottom: 1px solid ${UCSColors.gray05};
            width: 100%;
            display: flex;
            flex-direction: column;
            margin-bottom: 10px;
        `,
    },
    buttonWrapperMobile: {
        css: css`
            display: flex;
            margin-top: 10px;
        `,
    },
    dismissLink: {
        css: css`
            align-self: center;
            margin: 10px 0;
            color: ${getColor("primary")};
            span {
                color: ${getColor("primary")} !important;
                font-weight: 600;
            }
        `,
    },
    buttonStyleMobile: {
        css: css`
            background: #e5e5e5;
            border: none;
            display: flex;
            flex-direction: column;
            align-items: center;
            text-transform: uppercase;
            font-weight: 500;
            margin: 0 5px;
            padding: 0;
            color: black;
            font-size: 11px;
            &:hover {
                color: black !important;
            }
            span[class^="IconWrapper"] {
                background: white;
                margin: 0 5px 5px 0;
                color: black;
                border: 1px solid ${UCSColors.gray07};
                padding: 4px;
            }
        `,
    },
    buttonSelectedMobile: {
        css: css`
            background: #e5e5e5;
            border: none;
            display: flex;
            flex-direction: column;
            align-items: center;
            text-transform: uppercase;
            font-weight: 500;
            margin: 0 5px;
            padding: 0;
            color: black;
            font-size: 11px;
            &:hover {
                color: black !important;
            }
            span[class^="IconWrapper"] {
                background: ${UCSColors.gray07};
                margin: 0 5px 5px 0;
                color: #007a53;
                border: 2px solid black;
                padding: 4px;
            }
        `,
    },
    productSelection: {
        css: css`
            display: flex;
        `,
    },
    tooltipWrapper: {
        css: css`
            border: 2px solid ${getColor("primary")};
            width: 22px;
            height: 22px;
            display: inline-block;
            border-radius: 50%;
            padding: 0px 2px;
            margin-left: 5px;
            svg {
                color: ${getColor("primary")};
                top: -1px;
                position: relative;
            }
        `,
    },
    contentWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            #paginationTop {
                font-weight: 600;
            }
        `,
    },
};

const styles = locationFilterStyles;

const LocationFilter: FC<Props> = ({
    id,
    fields,
    addProductFilters,
    clearAllProductFilters,
    removeProductFilters,
    selectedAttributesIds,
    setIsLocalSelectedPLP,
    isLocalSelectedPLPState,
    isLoading,
    attributeTypeFacets,
}) => {
    const locationFilterId = getCookie("LocationBasedAttributeValue") || "";
    const [showDescription, setShowDescription] = useState(false);
    const [locationAccessNotGiven, setLocationAccessNotGiven] = useState(false);
    const locationFacets = attributeTypeFacets?.filter(x => x.name.toUpperCase().includes("LOCATION"));
    const combinedLocations: AttributeValueFacetModel[] = [];
    const toasterContext = useContext(ToasterContext);

    if (locationFacets) {
        locationFacets.forEach(item =>
            item.attributeValueFacets?.forEach(attributeValueFacet => combinedLocations.push(attributeValueFacet)),
        );
    }

    useEffect(() => {
        let isLocal = false;
        if (combinedLocations && selectedAttributesIds && locationFacets) {
            isLocal = isLocationPresentAndSingleMatch(combinedLocations, selectedAttributesIds);
            setIsLocalSelectedPLP({ isLocalSelectedPLP: isLocal });
        }
    }, [selectedAttributesIds]);

    useEffect(() => {
        setIsLocalSelectedPLP({ isLocalSelectedPLP: isLocationAvailable() });

        if (isLocalSelectedPLPState === true) {
            clearAllProductFilters();
            addProductFilters({ attributeValueIds: [locationFilterId] });
        }
        if (locationFilterId === "") {
            setLocationAccessNotGiven(true);
        } else {
            setLocationAccessNotGiven(false);
        }
    }, [locationFilterId]);

    const isLocationPresentAndSingleMatch = (
        locationFacets: AttributeValueFacetModel[],
        selectedAttributesIds: string[],
    ) => {
        const matches = locationFacets.filter(item1 =>
            selectedAttributesIds.some(item2 => item2 === item1.attributeValueId),
        );

        if (matches.length !== 1) {
            return false;
        }

        const locationInArray = matches[0].attributeValueId === locationFilterId;

        return locationInArray;
    };

    const isLocationAvailable = () => {
        if (!locationFacets) {
            return false;
        }
        let exists = false;
        locationFacets?.forEach(item => {
            if (item.attributeValueFacets?.find(location => location.attributeValueId === locationFilterId)) {
                exists = true;
            }
        });
        return exists;
    };
    const localClickHandler = () => {
        if (!isLocationAvailable()) {
            toasterContext.addToast({
                children: <>&nbsp;{siteMessage("LocationHasNoProduct")}</>,
                messageType: "warning",
                timeoutLength: 6000,
            });
            return;
        }
        setIsLocalSelectedPLP({ isLocalSelectedPLP: true });

        if (locationFilterId === "") {
            getCoordinates().then(() => {
                window.location.reload();
            });
            return;
        }
        if (!selectedAttributesIds) {
            clearAllProductFilters();
            addProductFilters({ attributeValueIds: [locationFilterId] });
        } else if (
            combinedLocations &&
            locationFacets &&
            selectedAttributesIds?.length >= 1 &&
            !isLocationPresentAndSingleMatch(combinedLocations, selectedAttributesIds)
        ) {
            removeProductFilters({ attributeValueIds: combinedLocations.map(x => x.attributeValueId) });
            addProductFilters({ attributeValueIds: [locationFilterId] });
        }
    };

    const globalClickHandler = () => {
        setIsLocalSelectedPLP({ isLocalSelectedPLP: false });
        if (locationFacets && combinedLocations && selectedAttributesIds) {
            const matches = combinedLocations.filter(item1 =>
                selectedAttributesIds.some(item2 => item2 === item1.attributeValueId),
            );
            removeProductFilters({ attributeValueIds: matches.map(x => x.attributeValueId) });
            return;
        }
        clearAllProductFilters();
    };

    const dismissClickHandler = () => {
        setShowDescription(false);
        setCookie("ShowLocationFilterDescription", "false");
    };

    const getCoordinates = () => {
        return new Promise(function (resolve, reject) {
            navigator.geolocation.getCurrentPosition(resolve, reject);
        });
    };

    return (
        <StyledWrapper {...styles.wrapperStyles}>
            <StyledWrapper {...styles.textWrapper}>
                <Typography>{fields.description}</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.buttonWrapper}>
                <Button
                    disabled={isLoading}
                    variant="secondary"
                    onClick={localClickHandler}
                    {...(isLocalSelectedPLPState === true && !locationAccessNotGiven
                        ? { ...styles.buttonSelected }
                        : { ...styles.buttonStyle })}
                >
                    <Icon
                        src={isLocalSelectedPLPState === true && isLoading ? LoadingSpinner : LocationIcon}
                        size={20}
                    />
                    <Typography>{translate("Local")}</Typography>
                </Button>
                <Button
                    disabled={isLoading}
                    variant="secondary"
                    onClick={globalClickHandler}
                    {...(isLocalSelectedPLPState === false || locationAccessNotGiven
                        ? { ...styles.buttonSelected }
                        : { ...styles.buttonStyle })}
                >
                    <Icon
                        src={
                            (isLocalSelectedPLPState === false && isLoading) || locationAccessNotGiven
                                ? LoadingSpinner
                                : GlobeIcon
                        }
                        size={20}
                    />
                    <Typography>{translate("Global")}</Typography>
                </Button>
            </StyledWrapper>
            <Hidden above="sm" style={{ width: "100%" }}>
                <StyledWrapper {...styles.mobileWrapper}>
                    {showDescription && (
                        <StyledWrapper {...styles.textWrapperMobile}>
                            <Typography>{fields.description}</Typography>
                            <Link onClick={dismissClickHandler} {...styles.dismissLink}>
                                Dismiss
                            </Link>
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.productSelection}>
                        <Typography>{translate("Product View")}:</Typography>
                        <Link {...styles.tooltipWrapper} onClick={() => setShowDescription(true)}>
                            <Icon src={QuestionMarkIcon} size={14} />
                        </Link>
                    </StyledWrapper>
                    <StyledWrapper {...styles.contentWrapper}>
                        <StyledWrapper {...styles.buttonWrapperMobile}>
                            <Button
                                disabled={isLoading}
                                variant="secondary"
                                onClick={localClickHandler}
                                {...(isLocalSelectedPLPState === true
                                    ? { ...styles.buttonSelectedMobile }
                                    : { ...styles.buttonStyleMobile })}
                            >
                                <Icon
                                    src={isLocalSelectedPLPState && isLoading ? LoadingSpinner : LocationIcon}
                                    size={40}
                                />
                                <Typography>{translate("Local")}</Typography>
                            </Button>
                            <Button
                                disabled={isLoading}
                                variant="secondary"
                                onClick={globalClickHandler}
                                {...(isLocalSelectedPLPState === false
                                    ? { ...styles.buttonSelectedMobile }
                                    : { ...styles.buttonStyleMobile })}
                            >
                                <Icon
                                    src={isLocalSelectedPLPState && isLoading ? LoadingSpinner : GlobeIcon}
                                    size={40}
                                />
                                <Typography>{translate("Global")}</Typography>
                            </Button>
                        </StyledWrapper>
                        <StyledWrapper>
                            <Zone zoneName="PaginationCount" contentId={id}></Zone>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </Hidden>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(LocationFilter),
    definition: {
        group: "Product List",
        displayName: "Location Filter",
        icon: "Button",
        fieldDefinitions: [
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
